import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The live border-box height of one element, for laying other things out below it (the
 * forecast header: the timeline's line labels and spine sit under it).
 *
 * Returns a CALLBACK ref, so the height follows the element as it mounts and unmounts — an
 * unmounted element reads 0 — and a ResizeObserver keeps it current through wrapping, a panel
 * opening, a font load or a display-size change.
 *
 * @returns {{ref: (el: HTMLElement|null) => void, height: number}}
 */
export function useMeasuredHeight() {
  const [height, setHeight] = useState(0);
  const observerRef = useRef(null);

  const ref = useCallback((el) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el) {
      setHeight(0);
      return;
    }
    const read = () => {
      const h = Math.round(el.offsetHeight);
      setHeight((prev) => (prev === h ? prev : h));
    };
    read();
    if (typeof ResizeObserver !== "undefined") {
      observerRef.current = new ResizeObserver(read);
      observerRef.current.observe(el);
    }
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return { ref, height };
}
